/**
 * web/src/games/useRound.ts — the data layer for Guess Me (card eq92fFXqQUuP).
 *
 * Three exports, deliberately thin:
 *
 *   useRound()     the round in play, refetched whenever the room says it moved
 *   useRoundList() the rounds already played plus the couple's scores
 *   submit()       one POST to the guess-me routes, returning the new round
 *
 * Nothing here decides anything: no verdict, no role, no cap. The screen shows
 * what the server says and the server is the only thing that knows who is
 * answering, whether the day is used up, and what the guess was worth.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { GuessMeCategory, GuessMeRound } from '../../../shared/types';
import { api } from '../shell/bridge';

const BASE = '/api/game/guess-me';

/** Pull the wire's error code back out of the thrown message. */
function codeOf(e: unknown): string | null {
  if (!(e instanceof Error)) return null;
  try {
    return (JSON.parse(e.message) as { error?: { code?: string } })?.error?.code ?? null;
  } catch {
    return null;
  }
}

/** One POST to a guess-me route. GET when there is no body (the bridge picks
 *  the method from that), POST otherwise — every guess-me write takes a body. */
export function submit<T>(path: string, body?: Record<string, unknown>): Promise<T> {
  return api<T>(`${BASE}${path}`, body ? { body } : {});
}

/** Milliseconds until the next UTC midnight — the moment the daily allowance
 *  resets, so the panel can stop telling a couple "that is all three" and
 *  offer the next round without a reload (sol review 2026-10-19). */
function untilNextUtcMidnight(): number {
  const now = new Date();
  const next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  return next - now.getTime();
}

/** The round in play — null when the couple has nothing open. Live: the room
 *  broadcasts t:'guessme' and useThread turns it into pear:guessme, which is
 *  just a reason to re-read, never the state itself. */
export function useRound() {
  const [round, setRound] = useState<GuessMeRound | null>(null);
  const [loaded, setLoaded] = useState(false);
  /* A failed initial fetch must not masquerade as "no round exists": the
     panel shows a retry instead of a Start button, and a later refresh that
     fails preserves what is already on screen (sol review 2026-10-19). */
  const [loadError, setLoadError] = useState(false);
  /* A Plus-gated read refuses with plus_required, which is not a connection
     problem and must not be rendered as one — the panel opens the upgrade
     sheet instead of a retry loop of further gated reads (sol review
     2026-10-19). */
  const [gated, setGated] = useState(false);
  /* Broadcast-triggered, action-triggered and initial reads can overlap; an
     older A_locked response arriving after a newer revealed one used to win
     with no further event to repair the panel. Each reload owns a generation
     and every state write checks it still does. */
  const gen = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++gen.current;
    try {
      const data = await submit<{ round: GuessMeRound | null }>('/round');
      if (mine !== gen.current) return;
      setRound(data.round ?? null);
      setLoadError(false);
      setGated(false);
    } catch (e) {
      // An abort is the panel unmounting mid-fetch — navigation, not a fault.
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        const code = codeOf(e);
        if (code === 'plus_required') {
          // A gate refusal is definitive, not a transient: clear any earlier
          // connection error so the two states never render together (sol
          // review 2026-10-19).
          if (mine === gen.current) {
            setGated(true);
            setLoadError(false);
          }
          return;
        }
        console.error('[Pear] guess-me round load failed', e);
        if (mine === gen.current) setLoadError(true);
      }
    } finally {
      if (mine === gen.current) setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const onGuessMe = () => void reload();
    window.addEventListener('pear:guessme', onGuessMe);
    return () => window.removeEventListener('pear:guessme', onGuessMe);
  }, [reload]);

  /* A missed broadcast while the tab was backgrounded (or a socket dropped)
     must not strand the screen: re-read on focus and visibility so a reveal
     that happened in the other partner's client lands here too, and refresh
     when the connection returns (sol review 2026-10-19). */
  useEffect(() => {
    const onResume = () => void reload();
    window.addEventListener('focus', onResume);
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('online', onResume);
    const onReconnect = () => void reload();
    window.addEventListener('pear:reconnected', onReconnect);
    return () => {
      window.removeEventListener('focus', onResume);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('online', onResume);
      window.removeEventListener('pear:reconnected', onReconnect);
    };
  }, [reload]);

  /* The daily allowance is server-side and counted in UTC; an app left open
     overnight would keep showing "0 left" until some unrelated event reloaded
     it. Wake once at the reset, refresh, and arm the next one (sol review
     2026-10-19). */
  useEffect(() => {
    let t = 0;
    const tick = () => {
      t = window.setTimeout(() => {
        void reload();
        tick();
      }, untilNextUtcMidnight());
    };
    tick();
    return () => window.clearTimeout(t);
  }, [reload]);

  return { round, loaded, loadError, gated, reload };
}

/** Rounds already played, newest first, plus the couple's per-category score.
 *  An unrevealed round in the list carries only your own side — the server
 *  strips the rest, so this hook has nothing to hide and nothing to leak. */
export function useRoundList(limit = 5) {
  const [rounds, setRounds] = useState<GuessMeRound[]>([]);
  const [scores, setScores] = useState<Partial<Record<GuessMeCategory, number>>>({});
  const [loaded, setLoaded] = useState(false);
  const gen = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++gen.current;
    try {
      const data = await submit<{ rounds: GuessMeRound[] }>(`/history?limit=${limit}`);
      if (mine === gen.current) setRounds(data.rounds ?? []);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        console.error('[Pear] guess-me history load failed', e);
      }
    }
    try {
      const data = await submit<{ scores: Partial<Record<GuessMeCategory, number>> }>('/scores');
      if (mine === gen.current) setScores(data.scores ?? {});
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        console.error('[Pear] guess-me scores load failed', e);
      }
    } finally {
      if (mine === gen.current) setLoaded(true);
    }
  }, [limit]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const onGuessMe = () => void reload();
    window.addEventListener('pear:guessme', onGuessMe);
    return () => window.removeEventListener('pear:guessme', onGuessMe);
  }, [reload]);

  useEffect(() => {
    const onResume = () => void reload();
    window.addEventListener('focus', onResume);
    document.addEventListener('visibilitychange', onResume);
    const onReconnect = () => void reload();
    window.addEventListener('pear:reconnected', onReconnect);
    return () => {
      window.removeEventListener('focus', onResume);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('pear:reconnected', onReconnect);
    };
  }, [reload]);

  return { rounds, scores, loaded, reload };
}
